import type {
  OpsAppliedSource,
  OpsCheckpointState,
  OpsFlow,
  OpsTaskState,
  OpsTemplateCheckpoint,
  OpsTemplateTask,
} from '../types';

type CheckpointDefWithMeta = OpsTemplateCheckpoint & {
  // earliest section this checkpoint belongs to in the flow UI
  firstSectionKey: string; // 'intake' or `source:${idx}`
  // most recent section that updated the checkpoint metadata
  lastUpdatedSectionKey: string;
};

type TaskDefWithMeta = OpsTemplateTask & {
  firstSectionKey: string;
  lastUpdatedSectionKey: string;
};

export type OpsFlowSection = {
  key: string; // 'intake' | `source:${idx}`
  title: string;
  sourceType: 'intake' | 'template';
  sourceId?: string;
  checkpoints: Array<{
    checkpoint: CheckpointDefWithMeta;
    tasks: TaskDefWithMeta[];
  }>;
};

function ensureCheckpointState(existing?: OpsCheckpointState): OpsCheckpointState {
  return existing || { status: 'not_started' };
}

function ensureTaskState(existing?: OpsTaskState): OpsTaskState {
  return existing || { status: 'open' };
}

/**
 * Ensure a flow has state entries for every checkpoint/task in a snapshot.
 * Preserves existing state; only fills missing keys with defaults.
 */
export function ensureFlowStateForSnapshot(
  flow: OpsFlow,
  snapshot: { checkpoints?: OpsTemplateCheckpoint[] } | null | undefined
): OpsFlow {
  const checkpointState: Record<string, OpsCheckpointState> = { ...(flow.checkpointState || {}) };
  const taskState: Record<string, OpsTaskState> = { ...(flow.taskState || {}) };

  const cps = (snapshot && Array.isArray((snapshot as any).checkpoints)) ? (snapshot as any).checkpoints as OpsTemplateCheckpoint[] : [];
  for (const cp of cps) {
    checkpointState[cp.checkpointId] = ensureCheckpointState(checkpointState[cp.checkpointId]);
    for (const t of cp.tasks || []) {
      taskState[t.taskId] = ensureTaskState(taskState[t.taskId]);
    }
  }

  return { ...flow, checkpointState, taskState };
}

/**
 * Build the merged view model:
 * - Intake section always at top
 * - Each applied source gets a section
 * - IDs are unique: checkpoint/task appears once, in the section where first introduced
 * - Metadata (title/helpText/order) comes from the most recently applied snapshot that mentions the ID
 */
export function buildFlowSections(params: {
  flow: OpsFlow;
  hideSuperseded?: boolean;
  includeIntake?: boolean;
}): OpsFlowSection[] {
  const { flow, hideSuperseded = true, includeIntake = true } = params;

  const sections: OpsFlowSection[] = [];

  const checkpointDefsById = new Map<string, CheckpointDefWithMeta>();
  const taskDefsById = new Map<string, TaskDefWithMeta>();

  const checkpointFirstSection = new Map<string, string>();
  const taskFirstSection = new Map<string, string>();

  const applySnapshot = (sectionKey: string, snapshot: { checkpoints: OpsTemplateCheckpoint[] } | null | undefined) => {
    const cps = (snapshot && Array.isArray((snapshot as any).checkpoints)) ? (snapshot as any).checkpoints as OpsTemplateCheckpoint[] : [];
    for (const cp of cps) {
      const existingCp = checkpointDefsById.get(cp.checkpointId);
      if (!checkpointFirstSection.has(cp.checkpointId)) checkpointFirstSection.set(cp.checkpointId, sectionKey);
      const firstSectionKey = checkpointFirstSection.get(cp.checkpointId)!;

      checkpointDefsById.set(cp.checkpointId, {
        ...(existingCp || (cp as any)),
        ...cp, // latest metadata wins
        firstSectionKey,
        lastUpdatedSectionKey: sectionKey,
      });

      for (const t of cp.tasks || []) {
        const existingT = taskDefsById.get(t.taskId);
        if (!taskFirstSection.has(t.taskId)) taskFirstSection.set(t.taskId, sectionKey);
        const tFirst = taskFirstSection.get(t.taskId)!;
        taskDefsById.set(t.taskId, {
          ...(existingT || (t as any)),
          ...t,
          firstSectionKey: tFirst,
          lastUpdatedSectionKey: sectionKey,
        });
      }
    }
  };

  // 1) Apply intake snapshot first (optional)
  if (includeIntake) {
    applySnapshot('intake', (flow as any).intakeSnapshot || null);
  }

  // 2) Apply sources in order (metadata precedence)
  (flow.appliedSources || []).forEach((src, idx) => {
    const key = `source:${idx}`;
    applySnapshot(key, (src as any).snapshot || null);
  });

  // 3) Build sections: optional intake + each source, but only include checkpoints introduced in that section
  if (includeIntake) {
    const intakeSection: OpsFlowSection = {
      key: 'intake',
      title: String(((flow as any).intakeSnapshot && (flow as any).intakeSnapshot.title) || 'Intake'),
      sourceType: 'intake',
      checkpoints: [],
    };
    sections.push(intakeSection);
  }

  (flow.appliedSources || []).forEach((src, idx) => {
    sections.push({
      key: `source:${idx}`,
      title: (src as any)?.snapshot?.title || 'Template',
      sourceType: 'template',
      sourceId: (src as any)?.sourceId,
      checkpoints: [],
    });
  });

  const bySection = new Map(sections.map((s) => [s.key, s]));

  // Determine which tasks belong to which checkpoints, but place tasks in the checkpoint's section of introduction
  const allCheckpointIds = Array.from(checkpointDefsById.keys());
  for (const checkpointId of allCheckpointIds) {
    const cp = checkpointDefsById.get(checkpointId)!;
    const state = flow.checkpointState?.[checkpointId];
    if (hideSuperseded && state?.status === 'superseded') continue;

    const sectionKey = cp.firstSectionKey;
    const section = bySection.get(sectionKey);
    if (!section) continue;

    const taskIds = (cp.tasks || []).map((t) => t.taskId);
    const tasks: TaskDefWithMeta[] = taskIds
      .map((id) => taskDefsById.get(id))
      .filter(Boolean) as TaskDefWithMeta[];

    const filteredTasks = tasks.filter((t) => {
      const st = flow.taskState?.[t.taskId];
      if (hideSuperseded && st?.status === 'superseded') return false;
      return true;
    });

    section.checkpoints.push({ checkpoint: cp, tasks: filteredTasks });
  }

  // Sort checkpoints within sections by their (latest) order, then title
  for (const s of sections) {
    s.checkpoints.sort((a, b) => {
      const ao = Number(flow.checkpointState?.[a.checkpoint.checkpointId]?.orderOverride ?? a.checkpoint.order ?? 0);
      const bo = Number(flow.checkpointState?.[b.checkpoint.checkpointId]?.orderOverride ?? b.checkpoint.order ?? 0);
      if (ao !== bo) return ao - bo;
      return String(a.checkpoint.title || '').localeCompare(String(b.checkpoint.title || ''));
    });
    for (const row of s.checkpoints) {
      row.tasks.sort((a, b) => {
        const ao = Number(a.order ?? 0);
        const bo = Number(b.order ?? 0);
        if (ao !== bo) return ao - bo;
        return String(a.title || '').localeCompare(String(b.title || ''));
      });
    }
  }

  return sections;
}

/**
 * Append an applied source to a flow and ensure state keys exist.
 * This does NOT compute summary fields; callers may do that separately.
 */
export function applySourceToFlow(params: {
  flow: OpsFlow;
  source: OpsAppliedSource;
}): OpsFlow {
  const { flow, source } = params;
  const next: OpsFlow = {
    ...flow,
    appliedSources: [...(flow.appliedSources || []), source],
  };
  const withState = ensureFlowStateForSnapshot(next, source.snapshot);

  // If a checkpoint/task was previously removed (superseded) in this flow,
  // and the user applies a new template that includes it again, "revive" it.
  // Otherwise it stays hidden by default (hideSuperseded=true) and appears as if
  // the template didn't include the checkpoint.
  const checkpointState: Record<string, OpsCheckpointState> = { ...(withState.checkpointState || {}) };
  const taskState: Record<string, OpsTaskState> = { ...(withState.taskState || {}) };
  const cps =
    source.snapshot && Array.isArray((source.snapshot as any).checkpoints)
      ? ((source.snapshot as any).checkpoints as OpsTemplateCheckpoint[])
      : [];

  for (const cp of cps) {
    const cpId = String(cp?.checkpointId || '').trim();
    if (!cpId) continue;
    const st = checkpointState[cpId];
    if (st?.status === 'superseded') {
      checkpointState[cpId] = { ...st, status: 'not_started' };
    }
    for (const t of cp.tasks || []) {
      const taskId = String((t as any)?.taskId || '').trim();
      if (!taskId) continue;
      const ts = taskState[taskId];
      if (ts?.status === 'superseded') {
        taskState[taskId] = { ...ts, status: 'open' };
      }
    }
  }

  return { ...withState, checkpointState, taskState };
}

